import React, { useState, useEffect } from 'react';
import {
  Card,
  Calendar,
  Modal,
  Select,
  Button,
  message,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  DatePicker,
  Space,
  Typography,
  Popconfirm,
  Spin
} from 'antd';
import {
  CalendarOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {
  getOffDatesGroupedByDate,
  getOffDatesStats,
  updateOffDate,
  removeOffDate,
  addOffDate,
  getEmployees
} from '../../../api/supabase';
import dayjs from 'dayjs';
import theme from '../../../theme/globalTheme';

const { Option } = Select;
const { Title, Text } = Typography;

const OffDatesViewer = () => {
  const [offDatesData, setOffDatesData] = useState({});
  const [stats, setStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    loadOffDatesForMonth(currentMonth);
  }, [currentMonth]);

  const loadEmployees = async () => {
    const { data, error } = await getEmployees();
    if (!error && data) {
      setEmployees(data.filter(emp => emp.status === 'active'));
    }
  };

  const loadOffDatesForMonth = async (date) => {
    setLoading(true);
    const startOfMonth = date.startOf('month').format('YYYY-MM-DD');
    const endOfMonth = date.endOf('month').format('YYYY-MM-DD');

    try {
      const { data: grouped, error: groupError } = await getOffDatesGroupedByDate(startOfMonth, endOfMonth);
      const { data: statsData, error: statsError } = await getOffDatesStats(startOfMonth, endOfMonth);

      if (!groupError && grouped) {
        setOffDatesData(grouped);
      }

      if (!statsError && statsData) {
        setStats(statsData);
      }
    } catch (error) {
      message.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      ASTREINTE: { color: '#722ed1', bg: '#f9f0ff', icon: '🚨' },
      BASE: { color: '#1890ff', bg: '#e6f7ff', icon: '🏢' },
      SEABOB: { color: '#13c2c2', bg: '#e6fffb', icon: '🌊' },
      JETSKI: { color: '#52c41a', bg: '#f6ffed', icon: '🏄' },
      BUREAU: { color: '#fa8c16', bg: '#fff7e6', icon: '💼' },
      ST_KITTS: { color: '#eb2f96', bg: '#fff0f6', icon: '🏝️' },
      OFF: { color: '#8c8c8c', bg: '#f5f5f5', icon: '🏖️' }
    };
    return colors[category] || colors.OFF;
  };

  const dateCellRender = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const dayData = offDatesData[dateStr] || [];

    if (dayData.length === 0) return null;

    // Count by category
    const categoryCounts = dayData.reduce((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {});

    const categoryEntries = Object.entries(categoryCounts);

    // Ultra-simple: just show colored dots in a row
    return (
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '3px',
        justifyContent: 'center',
        marginTop: '4px'
      }}>
        {categoryEntries.map(([cat, count]) => {
          const config = getCategoryColor(cat);
          return (
            <div
              key={cat}
              title={`${cat}: ${count}`}
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: config.color,
                boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
              }}
            />
          );
        })}
      </div>
    );
  };

  const onDateSelect = (date) => {
    setSelectedDate(date);
    setModalVisible(true);
  };

  const handleUpdateCategory = async (offDateId, newCategory) => {
    const { error } = await updateOffDate(offDateId, { category: newCategory });

    if (error) {
      message.error('Erreur lors de la mise à jour');
    } else {
      message.success('Catégorie mise à jour');
      loadOffDatesForMonth(currentMonth);
    }
  };

  const handleDeleteOffDate = async (offDateId) => {
    const { error} = await removeOffDate(offDateId);

    if (error) {
      message.error('Erreur lors de la suppression');
    } else {
      message.success('Entrée supprimée');
      loadOffDatesForMonth(currentMonth);
    }
  };

  const handleAddOffDate = async (employeeId, category) => {
    if (!selectedDate) return;

    const { error } = await addOffDate(employeeId, selectedDate.format('YYYY-MM-DD'), category);

    if (error) {
      message.error('Erreur lors de l\'ajout');
    } else {
      message.success('Employé ajouté');
      loadOffDatesForMonth(currentMonth);
    }
  };

  const handleExportToExcel = () => {
    try {
      // Prepare data for Excel export
      const exportData = [];

      // Sort dates
      const sortedDates = Object.keys(offDatesData).sort();

      // Build rows for each date
      sortedDates.forEach(date => {
        const dayEntries = offDatesData[date];

        // Group by category
        const byCategory = {
          ASTREINTE: [],
          BASE: [],
          SEABOB: [],
          JETSKI: [],
          BUREAU: [],
          ST_KITTS: [],
          OFF: []
        };

        dayEntries.forEach(entry => {
          if (byCategory[entry.category]) {
            byCategory[entry.category].push(entry.employee?.name || 'N/A');
          }
        });

        // Create row with emojis for better readability
        exportData.push({
          '📅 Date': dayjs(date).format('DD/MM/YYYY'),
          '📆 Jour': dayjs(date).format('dddd').charAt(0).toUpperCase() + dayjs(date).format('dddd').slice(1),
          '🚨 Astreinte': byCategory.ASTREINTE.join(', ') || '-',
          '🏢 Base': byCategory.BASE.join(', ') || '-',
          '🏄 Seabob': byCategory.SEABOB.join(', ') || '-',
          '🚤 Jetski': byCategory.JETSKI.join(', ') || '-',
          '💼 Bureau': byCategory.BUREAU.join(', ') || '-',
          '🏝️ St Kitts': byCategory.ST_KITTS.join(', ') || '-',
          '🏖️ Off': byCategory.OFF.join(', ') || '-'
        });
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for optimal readability
      ws['!cols'] = [
        { wch: 14 },  // Date
        { wch: 12 },  // Jour
        { wch: 30 },  // ASTREINTE
        { wch: 30 },  // BASE
        { wch: 30 },  // SEABOB
        { wch: 30 },  // JETSKI
        { wch: 30 },  // BUREAU
        { wch: 30 },  // ST KITTS
        { wch: 30 }   // OFF
      ];

      // Apply styling to cells
      const range = XLSX.utils.decode_range(ws['!ref']);

      // Style header row (row 0)
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;

        ws[cellAddress].s = {
          font: {
            bold: true,
            sz: 12,
            color: { rgb: "FFFFFF" }
          },
          fill: {
            fgColor: { rgb: "1890FF" }
          },
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true
          },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Style data rows with alternating colors
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const isEvenRow = row % 2 === 0;

        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;

          // Apply category-specific colors
          let fillColor = isEvenRow ? "F5F5F5" : "FFFFFF";
          let fontColor = "000000";

          // Date columns (0, 1)
          if (col <= 1) {
            fillColor = isEvenRow ? "E6F7FF" : "F0F9FF";
            ws[cellAddress].s = {
              font: {
                bold: true,
                sz: 11,
                color: { rgb: fontColor }
              },
              fill: {
                fgColor: { rgb: fillColor }
              },
              alignment: {
                horizontal: "center",
                vertical: "center"
              },
              border: {
                top: { style: "thin", color: { rgb: "D9D9D9" } },
                bottom: { style: "thin", color: { rgb: "D9D9D9" } },
                left: { style: "thin", color: { rgb: "D9D9D9" } },
                right: { style: "thin", color: { rgb: "D9D9D9" } }
              }
            };
          } else {
            // Category columns with color coding
            const cellValue = ws[cellAddress].v;
            const hasData = cellValue && cellValue !== '-';

            // Apply subtle background colors for non-empty cells
            if (hasData) {
              switch (col) {
                case 2: fillColor = "FFF7E6"; break; // Astreinte - Orange tint
                case 3: fillColor = "E6F7FF"; break; // Base - Blue tint
                case 4: fillColor = "E6FFFB"; break; // Seabob - Cyan tint
                case 5: fillColor = "F0F5FF"; break; // Jetski - Geekblue tint
                case 6: fillColor = "F9F0FF"; break; // Bureau - Purple tint
                case 7: fillColor = "FFF1F0"; break; // St Kitts - Red tint
                case 8: fillColor = "F6FFED"; break; // Off - Green tint
              }
            }

            ws[cellAddress].s = {
              font: {
                sz: 10,
                color: { rgb: hasData ? "000000" : "BFBFBF" }
              },
              fill: {
                fgColor: { rgb: fillColor }
              },
              alignment: {
                horizontal: "left",
                vertical: "center",
                wrapText: true
              },
              border: {
                top: { style: "thin", color: { rgb: "D9D9D9" } },
                bottom: { style: "thin", color: { rgb: "D9D9D9" } },
                left: { style: "thin", color: { rgb: "D9D9D9" } },
                right: { style: "thin", color: { rgb: "D9D9D9" } }
              }
            };
          }
        }
      }

      // Set row heights
      ws['!rows'] = Array(range.e.r + 1).fill({ hpt: 25 });
      ws['!rows'][0] = { hpt: 35 }; // Header row taller

      // Freeze header row and date columns
      ws['!freeze'] = { xSplit: 2, ySplit: 1 };

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Planning Équipe');

      // Add a summary sheet
      const summaryData = [];
      const categoryStats = {
        '🚨 Astreinte': 0,
        '🏢 Base': 0,
        '🏄 Seabob': 0,
        '🚤 Jetski': 0,
        '💼 Bureau': 0,
        '🏝️ St Kitts': 0,
        '🏖️ Off': 0
      };

      // Calculate totals
      sortedDates.forEach(date => {
        const dayEntries = offDatesData[date];
        dayEntries.forEach(entry => {
          switch (entry.category) {
            case 'ASTREINTE': categoryStats['🚨 Astreinte']++; break;
            case 'BASE': categoryStats['🏢 Base']++; break;
            case 'SEABOB': categoryStats['🏄 Seabob']++; break;
            case 'JETSKI': categoryStats['🚤 Jetski']++; break;
            case 'BUREAU': categoryStats['💼 Bureau']++; break;
            case 'ST_KITTS': categoryStats['🏝️ St Kitts']++; break;
            case 'OFF': categoryStats['🏖️ Off']++; break;
          }
        });
      });

      // Create summary data
      summaryData.push({ 'Catégorie': '📊 STATISTIQUES DU MOIS', 'Total': '' });
      summaryData.push({ 'Catégorie': '', 'Total': '' });
      Object.entries(categoryStats).forEach(([cat, count]) => {
        summaryData.push({ 'Catégorie': cat, 'Total': count });
      });

      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Statistiques');

      // Generate filename with month name
      const monthName = currentMonth.format('MMMM_YYYY');
      const filename = `Planning_Equipe_${monthName}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);

      message.success(`📊 Export Excel réussi : ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      message.error('Erreur lors de l\'export Excel');
    }
  };

  const selectedDayData = selectedDate ? offDatesData[selectedDate.format('YYYY-MM-DD')] || [] : [];

  const columns = [
    {
      title: 'Employé',
      dataIndex: ['employee', 'name'],
      key: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '14px',
          }}>
            {name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <Text strong>{name}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.employee?.role}</Text>
          </div>
        </div>
      )
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
      render: (category, record) => {
        const config = getCategoryColor(category);
        return (
          <Select
            value={category}
            onChange={(value) => handleUpdateCategory(record.id, value)}
            style={{ width: 160 }}
            size="small"
          >
            {['ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF'].map(cat => {
              const catConfig = getCategoryColor(cat);
              return (
                <Option key={cat} value={cat}>
                  <Tag color={catConfig.color} style={{ margin: 0 }}>
                    {catConfig.icon} {cat}
                  </Tag>
                </Option>
              );
            })}
          </Select>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Supprimer cette entrée?"
          onConfirm={() => handleDeleteOffDate(record.id)}
          okText="Oui"
          cancelText="Non"
        >
          <Button danger size="small" icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
  ];

  return (
    <div style={{
      padding: theme.spacing.lg,
      minHeight: '100vh',
      background: theme.colors.bgSecondary,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing.lg,
        background: theme.colors.bgPrimary,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        boxShadow: theme.effects.shadowMd,
        border: `1px solid ${theme.colors.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: theme.borderRadius.lg,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
          }}>
            <CalendarOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
          </div>
          <div>
            <Title level={3} style={{ margin: 0 }}>Planning Employés</Title>
            <Text type="secondary">Visualisez et modifiez les catégories par jour</Text>
          </div>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExportToExcel}
          size="large"
          style={{
            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            border: 'none',
            borderRadius: theme.borderRadius.lg,
            boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)',
            fontWeight: theme.typography.fontWeight.semibold,
            height: '44px',
            paddingLeft: theme.spacing.lg,
            paddingRight: theme.spacing.lg,
          }}
        >
          Exporter en Excel
        </Button>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: theme.spacing.lg }}>
          {Object.entries(stats).filter(([key]) => key !== 'total').map(([category, count]) => {
            const config = getCategoryColor(category);
            return (
              <Col key={category} xs={24} sm={12} md={8} lg={6} xl={4}>
                <Card
                  style={{
                    borderLeft: `4px solid ${config.color}`,
                    background: config.bg,
                  }}
                  styles={{ body: { padding: theme.spacing.md } }}
                >
                  <Statistic
                    title={
                      <span style={{ fontSize: '12px', color: config.color, fontWeight: 600 }}>
                        {config.icon} {category}
                      </span>
                    }
                    value={count}
                    valueStyle={{ color: config.color, fontSize: '28px', fontWeight: 700 }}
                  />
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      {/* Calendar */}
      <Card
        style={{
          boxShadow: theme.effects.shadowLg,
          borderRadius: theme.borderRadius.lg,
        }}
      >
        <style>{`
          /* Clean calendar styling */
          .ant-picker-calendar-date-content {
            min-height: 30px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .ant-picker-cell-inner {
            padding: 12px 4px !important;
            min-height: 80px !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: flex-start !important;
            border-radius: 8px !important;
            transition: all 0.2s ease !important;
          }

          .ant-picker-cell-inner:hover {
            background: #f0f5ff !important;
            transform: scale(1.02) !important;
          }

          .ant-picker-calendar-date-value {
            font-weight: 700 !important;
            font-size: 18px !important;
            margin-bottom: 8px !important;
            color: #1f2937 !important;
            line-height: 1 !important;
          }

          .ant-picker-cell-selected .ant-picker-cell-inner {
            background: #e6f4ff !important;
          }

          .ant-picker-cell-today .ant-picker-calendar-date-value {
            color: #1890ff !important;
          }

          .ant-picker-calendar-header {
            padding: 16px 0 !important;
          }
        `}</style>
        <Spin spinning={loading}>
          <Calendar
            dateCellRender={dateCellRender}
            onSelect={onDateSelect}
            onPanelChange={(date) => setCurrentMonth(date)}
          />
        </Spin>
      </Card>

      {/* Day Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
            <CalendarOutlined style={{ color: theme.colors.primary }} />
            <span>
              {selectedDate ? selectedDate.format('DD MMMM YYYY') : ''}
            </span>
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={null}
      >
        <Table
          dataSource={selectedDayData}
          columns={columns}
          rowKey="id"
          pagination={false}
          size="small"
          style={{ marginTop: theme.spacing.md }}
        />

        <div style={{
          marginTop: theme.spacing.lg,
          padding: theme.spacing.md,
          background: theme.colors.bgSecondary,
          borderRadius: theme.borderRadius.md,
        }}>
          <Text strong>Ajouter un employé pour cette date:</Text>
          <Space style={{ marginTop: theme.spacing.sm, width: '100%' }}>
            <Select
              placeholder="Sélectionner un employé"
              style={{ width: 250 }}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              id="add-employee-select"
            >
              {employees.map(emp => (
                <Option key={emp.id} value={emp.id}>{emp.name}</Option>
              ))}
            </Select>
            <Select
              placeholder="Catégorie"
              style={{ width: 150 }}
              id="add-category-select"
            >
              {['ASTREINTE', 'BASE', 'SEABOB', 'JETSKI', 'BUREAU', 'ST_KITTS', 'OFF'].map(cat => {
                const config = getCategoryColor(cat);
                return (
                  <Option key={cat} value={cat}>
                    {config.icon} {cat}
                  </Option>
                );
              })}
            </Select>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                const empId = document.getElementById('add-employee-select')?.value;
                const category = document.getElementById('add-category-select')?.value;
                if (empId && category) {
                  handleAddOffDate(empId, category);
                } else {
                  message.warning('Sélectionnez un employé et une catégorie');
                }
              }}
            >
              Ajouter
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default OffDatesViewer;
